import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import IPage from '../../../interfaces/IPage';
import TenantDomain from '../steps/tenantDomain/TenantDomain';
import Identification from '../steps/identification/Identification';
import { Img, Link, LinkHowToUse, WizardContainer, WizardContent, WizardFooter } from './Wizard.styles';
import { Back } from './Wizard.styles';
import DocumentType from '../steps/documentType/DocumentType';

import imgBack from '../../../assets/img-back.png';
import logoSenior from '../../../assets/logo.png';
import ServiceLocation from '../steps/serviceLocation/ServiceLocation';
import IMedicalCertificate from '../../../interfaces/IMedicalCertificate';
import { useLocalStorage } from '../../../hooks/providers/useLocalStorage';
import ProviderServiceData from '../steps/providerServiceData/ProviderServiceData';
import DocumentDate from '../steps/documentDate/DocumentDate';
import Period from '../steps/period/Period';
import DiseaseClassification from '../steps/diseaseClassification/DiseaseClassification';
import MedicalCertificateImage from '../steps/medicalCertificateImage/MedicalCertificateImage';
import { useWizardRequest } from '../../../hooks/services/useWizardRequest';
import AppError from '../../../errors/AppError';
import Contact from '../steps/contact/Contact';
import Success from '../steps/success/Success';
import Error from '../steps/errors/error';
import ConfirmationData from '../steps/confirmationData/ConfirmationData';
import InstallPWA from '../../../components/InstallPWA/InstallPWA';
import moment from 'moment';
import AttestationOrPoint from '../steps/AttestationOrPoint/AttestationOrPoint';
import TimeRecordList from '../steps/timeRecord/timeRecordList/TimeRecordList';
import TimeRecordForm from '../steps/timeRecord/timeRecordForm/TimeRecordForm';
import Comments from '../steps/comments/Comments';
import LoginIcon from '@mui/icons-material/Login';
import PsychologyIcon from '@mui/icons-material/Psychology';
import toastr from 'toastr';

const initialMedicalCertificate: IMedicalCertificate = {
  typeDocumentId: 0,
  employeeId: 0,
  clinicName: '',
  professionalTypeId: 0,
  registerNumber: '',
  professionalName: '',
  documentDate: null,
  typeTime: 'DAY',
  startTime: String(new Date()),
  endTime: String(new Date()),
  timeQuantity: 0,
  diseaseClassifications: [],
  email: '',
  phoneNumber: '',
  status: 'PENDING',
  comments: '',
  notes: '',
  origin: '',
  statusIntegration: '',
  datetimeIntegraton: null,
  confirmed: false,
  confirmedAt: null,
  professionalTypeName: '',
  professionalTypeOrganization: '',
  professionalState: '',
  errorTitle: '',
  errorMessage: '',
};

interface IWizardProps extends IPage {
  step?: number;
}

const Wizard = (props: IWizardProps) => {
  const requests = useWizardRequest();
  window.document.title = props.title;
  const [searchParams, setSearchParams] = useSearchParams();

  const [medicalCertificate, setMedicalCertificate] = useState<IMedicalCertificate>({
    ...initialMedicalCertificate,
    employeeId: Number(searchParams.get('employeeId')),
  });

  const localStorage = useLocalStorage();

  const [currentStep, setCurrentStep] = useState(Number(searchParams.get('step')));
  const [previousStep, setPreviousStep] = useState(0);
  const [validSessionTime] = useState<number>(() => {
    return Number(process.env.REACT_APP_SESSION_TIME_IN_SECOUNDS) || 86400;
  });
  const [skip, setSkip] = useState(1);
  const navigate = useNavigate();
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const step = Number(searchParams.get('step'));
    const employeeId = searchParams.get('employeeId');

    setMedicalCertificate({ ...medicalCertificate, employeeId: Number(employeeId) });
    loadWizard(step);
    isInitialLoad.current = false;
  }, []);

  const loadWizard = (step: number | null) => {
    const medicalCertificateLocal = localStorage.getItem('medicalCertificate') || null;

    if (!medicalCertificateLocal) {
      setStep(step || 1, { ...initialMedicalCertificate });
    }

    setMedicalCertificate(state => {
      return {
        ...state,
      };
    });
    const savedStep = localStorage.getItem('step') || null;

    const localSettings = localStorage.getItem('localSettings');
    const tenantDomain = localSettings?.tenantDomain;

    if (!searchParams.get('tenantDomain') && tenantDomain) {
      setSearchParams({ tenantDomain });
    }

    if (!searchParams.get('tenantDomain') && !tenantDomain) {
      setStep(1, { ...initialMedicalCertificate });
    } else if ((savedStep?.step && searchParams.get('tenantDomain')) || tenantDomain) {
      setStep(savedStep?.step || step || 2, { ...medicalCertificateLocal });
    } else {
      setStep(step || 2, { ...initialMedicalCertificate });
    }
  };

  const instantiateMedicalCertificate = (medicalCertificate: IMedicalCertificate): IMedicalCertificate => {
    const resource: IMedicalCertificate = {
      ...(medicalCertificate.id && { id: medicalCertificate.id }),
      typeDocumentId: medicalCertificate.typeDocumentId,
      employeeId: medicalCertificate.employeeId,
      ...(medicalCertificate.employee && { employee: medicalCertificate.employee }),
      ...(medicalCertificate.typeDocument && { typeDocument: medicalCertificate.typeDocument }),
      clinicName: medicalCertificate.clinicName,
      professionalTypeId: medicalCertificate.professionalTypeId,
      registerNumber: medicalCertificate.registerNumber,
      professionalName: medicalCertificate.professionalName,
      documentDate: medicalCertificate.documentDate,
      typeTime: medicalCertificate.typeTime,
      startTime: moment(medicalCertificate.startTime).format(),
      endTime: moment(medicalCertificate.endTime).format(),
      timeQuantity: medicalCertificate.timeQuantity,
      diseaseClassifications: medicalCertificate.diseaseClassifications,
      email: medicalCertificate.email,
      phoneNumber: medicalCertificate.phoneNumber,
      status: 'PENDING',
      comments: medicalCertificate.comments,
      notes: medicalCertificate.notes,
      origin: 'WEBAPP',
      statusIntegration: null,
      datetimeIntegraton: null,
      confirmed: medicalCertificate.confirmed,
      confirmedAt: medicalCertificate.confirmedAt,
      professionalTypeName: medicalCertificate.professionalTypeName,
      professionalTypeOrganization: medicalCertificate.professionalTypeOrganization,
      professionalState: medicalCertificate.professionalState,
      companionName: medicalCertificate.companionName,
      companionRelationship: medicalCertificate.companionRelationship,
      companionAge: medicalCertificate.companionAge,
      alerts: medicalCertificate.alerts || [],
      ...(medicalCertificate.createdAt && { createdAt: medicalCertificate.createdAt }),
      ...(medicalCertificate.updatedAt && { updatedAt: medicalCertificate.updatedAt }),
      ...(medicalCertificate.deletedAt && { deletedAt: medicalCertificate.deletedAt }),
      isDraft: medicalCertificate.isDraft,
      protocolNumber: medicalCertificate.protocolNumber,
      errorTitle: medicalCertificate.errorTitle,
      errorMessage: medicalCertificate.errorMessage,
    };
    if (medicalCertificate.attachments && medicalCertificate.attachments?.length > 0) {
      resource.attachments = medicalCertificate.attachments;
    } else {
      resource.attachments = [];
    }
    return resource;
  };

  const saveMedicalCertificate = async (medicalCertificate: IMedicalCertificate): Promise<IMedicalCertificate> => {
    const resource: IMedicalCertificate = instantiateMedicalCertificate(medicalCertificate);

    if (resource.timeQuantity >= 15 * 24) {
      if (!resource.alerts) {
        resource.alerts = ['INSS'];
      } else {
        resource.alerts.indexOf('INSS') === -1 && resource.alerts.push('INSS');
      }
      resource.justifiedDays = resource.justifiedDays ?? 15;
    } else {
      resource.justifiedDays = 0;
    }

    if (resource.timeQuantity < 15 * 24 && resource.alerts && resource.alerts.indexOf('INSS') !== -1) {
      resource.alerts = resource.alerts.filter(alert => alert !== 'INSS');
    }

    delete resource.attachments;
    if (medicalCertificate?.id && medicalCertificate?.id?.split('').length > 9) {
      return await requests
        .updateMedicalCertificate(String(resource.id), resource)
        .then(result => {
          return instantiateMedicalCertificate(result);
        })
        .catch(error => {
          throw new AppError(error.message);
        });
    } else {
      return await requests
        .createMedicalCertificate(resource)
        .then(result => {
          return instantiateMedicalCertificate(result);
        })
        .catch(error => {
          throw new AppError(error.message);
        });
    }
  };

  const cleanLocalStorage = () => {
    localStorage.removeItem('expireIn');
    localStorage.removeItem('step');
    localStorage.removeItem('medicalCertificate');
    localStorage.removeItem('confirmationData');
    localStorage.removeItem('documentType');
    localStorage.removeItem('providerServiceDataState');
  };

  const setStep = useCallback(
    async (step: number, medicalCertificateParam?: IMedicalCertificate, previousStep?: number): Promise<void> => {
      setPreviousStep(previousStep || currentStep - 1);
      const now = moment();
      const expireInStorage = localStorage.getItem('expireIn') ? moment(localStorage.getItem('expireIn')) : null;

      if (!expireInStorage) {
        const expireIn = moment()
          .add(Number(validSessionTime), 'seconds')
          .format();
        localStorage.setItem('expireIn', expireIn);

        if (medicalCertificateParam) {
          localStorage.setItem('medicalCertificate', { ...medicalCertificateParam });
          setMedicalCertificate({ ...medicalCertificateParam });
          setCurrentStep(step);
        }
      } else if (expireInStorage.isSameOrBefore(now)) {
        cleanLocalStorage();
        if (!isInitialLoad.current) {
          toastr.error('Sua sessão expirou. Por favor, reinicie o processo.');
        }
        const expireIn = moment()
          .add(Number(validSessionTime), 'seconds')
          .format();

        localStorage.setItem('expireIn', expireIn);

        const localSettings = localStorage.getItem('localSettings') ? localStorage.getItem('localSettings') : null;

        if (localSettings && localSettings.tenantDomain) {
          setCurrentStep(2);
        } else {
          setCurrentStep(0);
        }
      } else {
        if (medicalCertificateParam) {
          if (step > 9 && step < 16) {
            // Enviando para o backend
            const resource = localStorage.getItem('medicalCertificate');
            await saveMedicalCertificate({ ...resource, ...medicalCertificateParam })
              .then(result => {
                setMedicalCertificate(state => {
                  localStorage.setItem('medicalCertificate', { ...state, ...result });
                  return {
                    ...state,
                    ...result,
                  };
                });
              })
              .catch(error => {
                console.error('Erro ao salvar atestado:', error.message);
              });
          } else if (step <= 3) {
            // Reiniciando o estado
            localStorage.setItem('medicalCertificate', { ...medicalCertificateParam });
            setMedicalCertificate({ ...medicalCertificateParam });
          } else {
            setMedicalCertificate(state => {
              localStorage.setItem('medicalCertificate', { ...state, ...medicalCertificateParam });
              return {
                ...state,
                ...medicalCertificateParam,
              };
            });
          }
        }
        setCurrentStep(step);

        const timeClocking = localStorage.getItem('useTimeClocking');
        setSkip(1);

        if (!timeClocking && step === 7) {
          setSkip(3);
        }

        if (timeClocking && step === 7) {
          setSkip(3);
        }

        if (!timeClocking && step === 4) {
          setSkip(2);
        }

        if (timeClocking && step === 6) {
          setSkip(3);
        }

        if (!medicalCertificateParam?.typeDocument?.allowComments && step === 15) {
          setSkip(2);
        }

        if (step !== 1) {
          localStorage.setItem('step', { step });
        } else {
          localStorage.removeItem('step');
        }
      }
    },
    [],
  );

  const renderStep = useCallback(() => {
    if (currentStep === 1) {
      return <TenantDomain setStep={setStep} step={1} />;
    }
    if (currentStep === 2) {
      cleanLocalStorage();
      return <Identification setStep={setStep} step={2} medicalCertificate={initialMedicalCertificate} />;
    }
    if (currentStep === 3) {
      return <AttestationOrPoint setStep={setStep} step={3} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 4) {
      return <ConfirmationData setStep={setStep} step={4} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 5) {
      return <TimeRecordList setStep={setStep} step={5} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 6) {
      return <TimeRecordForm setStep={setStep} step={6} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 7) {
      return <DocumentType setStep={setStep} step={7} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 8) {
      if (medicalCertificate.typeDocument?.showHospital === false) {
        if (medicalCertificate.typeDocument?.showCRM === true) {
          return <ProviderServiceData setStep={setStep} step={9} medicalCertificate={medicalCertificate} />;
        } else {
          return <DocumentDate setStep={setStep} step={10} medicalCertificate={medicalCertificate} />;
        }
      } else {
        return <ServiceLocation setStep={setStep} step={8} medicalCertificate={medicalCertificate} />;
      }
    }
    if (currentStep === 9) {
      if (medicalCertificate.typeDocument?.showCRM === false) {
        if (previousStep === 8) {
          return <DocumentDate setStep={setStep} step={10} medicalCertificate={medicalCertificate} />;
        } else {
          return <DocumentType setStep={setStep} step={7} medicalCertificate={medicalCertificate} />;
        }
      } else {
        return <ProviderServiceData setStep={setStep} step={9} medicalCertificate={medicalCertificate} />;
      }
    }
    if (currentStep === 10) {
      return <DocumentDate setStep={setStep} step={10} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 11) {
      return <Period setStep={setStep} step={11} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 12) {
      if (medicalCertificate.typeDocument?.showCID === false) {
        if (previousStep === 11) {
          return <MedicalCertificateImage setStep={setStep} step={13} medicalCertificate={medicalCertificate} />;
        } else {
          return <Period setStep={setStep} step={11} medicalCertificate={medicalCertificate} />;
        }
      } else {
        return <DiseaseClassification setStep={setStep} step={12} medicalCertificate={medicalCertificate} />;
      }
    }
    if (currentStep === 13) {
      return <MedicalCertificateImage setStep={setStep} step={13} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 14) {
      return <Comments setStep={setStep} step={14} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 15) {
      return <Contact setStep={setStep} step={15} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 16) {
      return <Success setStep={setStep} step={16} medicalCertificate={medicalCertificate} />;
    }
    if (currentStep === 17) {
      return <Error setStep={setStep} step={17} medicalCertificate={medicalCertificate} />;
    }
  }, [currentStep]);

  return (
    <WizardContainer>
      <WizardContent>
        {currentStep >= 3 && currentStep <= 15 && (
          <Back
            onClick={() => {
              if (currentStep === 4) {
                setStep(3);
                return;
              }
              setStep(currentStep - skip);
            }}
          >
            <img src={imgBack} alt="voltar" />
          </Back>
        )}
        {renderStep()}

          <InstallPWA type="link" />
          <Link onClick={() => navigate('/login')}>
            <LoginIcon/>
            Login
          </Link>
          <LinkHowToUse
            onClick={() => window.open('https://app.seniornortepr.com.br/docs/videos/EnviarAtestado.mp4', '_blank')}
          >
            <PsychologyIcon/>
            {' '}
            Como funciona?
          </LinkHowToUse>
        
      </WizardContent>
      <WizardFooter>
        <>
          <Img src={logoSenior} alt="Senior" />
          Senior Norte PR
        </>
      </WizardFooter>
    </WizardContainer>
  );
};

export default Wizard;
